import { Pressable, StyleSheet, View } from "react-native";

import {
  radii,
  spacing,
  palette,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { AppText } from "./AppText";
import { Row } from "./Row";
import { Stack } from "./Stack";

export type RadioOption<TValue extends string> = {
  label: string;
  value: TValue;
  description?: string;
  disabled?: boolean;
};

export function RadioGroup<TValue extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: TValue;
  options: Array<RadioOption<TValue>>;
  onChange: (value: TValue) => void;
}) {
  const { stylesheet } = useThemedStyles(makeStyles);
  return (
    <Stack gap="sm" accessibilityRole="radiogroup" accessibilityLabel={label}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="radio"
            accessibilityState={{ selected, disabled: option.disabled }}
            disabled={option.disabled}
            onPress={() => onChange(option.value)}
            style={({ hovered, pressed }) => [
              stylesheet.option,
              selected ? stylesheet.optionSelected : null,
              hovered || pressed ? stylesheet.optionHovered : null,
              option.disabled ? stylesheet.optionDisabled : null,
            ]}
          >
            <Row align="start" gap="sm">
              <View
                style={[
                  stylesheet.radio,
                  selected ? stylesheet.radioSelected : null,
                ]}
              />
              <Stack gap="xs" style={stylesheet.copy}>
                <AppText weight="semibold">{option.label}</AppText>
                {option.description ? (
                  <AppText size="xs">{option.description}</AppText>
                ) : null}
              </Stack>
            </Row>
          </Pressable>
        );
      })}
    </Stack>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    option: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      padding: spacing.m,
      backgroundColor: colors.background,
    },
    optionSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.mutedSurface,
    },
    optionHovered: { backgroundColor: colors.surface },
    optionDisabled: { opacity: 0.5 },
    radio: {
      width: 18,
      height: 18,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.pill,
      backgroundColor: colors.background,
    },
    radioSelected: {
      borderWidth: 5,
      borderColor: colors.primary,
      backgroundColor: palette.brandForest,
    },
    copy: { flex: 1 },
  });
  return { stylesheet };
};
